import {useCallback, useEffect, useMemo, useState} from 'react';
import type {MouseEvent} from 'react';
import {Alert, AlertTitle, Box, IconButton, Snackbar, Tab, Tabs, Tooltip} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import {PageHeader} from 'src/components/ui';
import {useOrgId} from 'src/hooks';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import {getApartmentList, selectProperty} from 'src/redux/slice/propertySlice';
import {tenantServiceFind, intercomDeviceServiceFind, buildingServiceFind} from 'src/apis/autoGenerate/services';
import {acsIntegrationClient} from 'src/apis/acsIntegrationClient';
import {Apartment} from 'src/apis/autoGenerate/types/apartment';
import {Building} from 'src/apis/autoGenerate/types/building';
import {IntercomDevice} from 'src/apis/autoGenerate/types/intercomDevice';
import {Tenant} from 'src/apis/autoGenerate/types/tenant';
import message from 'src/components/Message';
import {callTenantFromFrontDesk, listCbwDevices, openDoorFromFrontDesk} from 'src/apis/intercomOpsClient';
import ResidentsTab from './ResidentsTab';
import DevicesTab from './DevicesTab';
import ResidentCallPanel from './ResidentCallPanel';
import DoorControlCard from './DoorControlCard';
import {FrontDeskCallTarget, pickCallContextDevice} from './types';
import {
  dismissFrontDeskStartPageHint,
  HOME_PATH_FRONT_DESK,
  isFrontDeskHome,
  setHomePath,
  subscribeHomePath,
} from 'src/utils/contextPreferences';
import StartPageHintPopover from './StartPageHintPopover';

export default function FrontDeskPage() {
  const {orgId} = useOrgId();
  const account = useAppSelector(selectAccount);
  const property = useAppSelector(selectProperty);
  const apartments = (property.apartmentList || []) as Apartment[];

  const [tab, setTab] = useState(0);
  const [propertyId, setPropertyId] = useState('all');
  const [buildingId, setBuildingId] = useState('all');
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [devices, setDevices] = useState<IntercomDevice[]>([]);
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [integrationByDeviceId, setIntegrationByDeviceId] = useState<Record<string, string>>({});
  const [loadingResidents, setLoadingResidents] = useState(false);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [callingTenantId, setCallingTenantId] = useState<string>();
  const [openingDeviceId, setOpeningDeviceId] = useState<string>();
  const [activeCall, setActiveCall] = useState<FrontDeskCallTarget | null>(null);
  const [doorOpenedName, setDoorOpenedName] = useState<string | null>(null);
  const [frontDeskIsHome, setFrontDeskIsHome] = useState(() =>
    isFrontDeskHome(account.userId, account.role?.name)
  );
  const [startPageToastOpen, setStartPageToastOpen] = useState(false);
  const [startPageHintAnchor, setStartPageHintAnchor] = useState<HTMLElement | null>(null);

  useEffect(() => {
    const syncHome = () => setFrontDeskIsHome(isFrontDeskHome(account.userId, account.role?.name));
    syncHome();
    return subscribeHomePath(syncHome);
  }, [account.userId, account.role?.name]);

  const apartmentIds = useMemo(
    () => apartments.map(apartment => apartment.id).filter(Boolean) as string[],
    [apartments]
  );

  const loadResidents = useCallback(async () => {
    if (!orgId || apartmentIds.length === 0) {
      setTenants([]);
      return;
    }
    setLoadingResidents(true);
    try {
      const results = await Promise.allSettled(
        apartmentIds.map(apartmentId => tenantServiceFind(undefined, apartmentId, 1000, 1, `${orgId}`))
      );
      const next: Tenant[] = [];
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value?.data?.tenants) {
          next.push(...result.value.data.tenants);
        }
      });
      setTenants(next);
    } catch (error) {
      console.error(error);
      message.error('Failed to load residents.');
      setTenants([]);
    } finally {
      setLoadingResidents(false);
    }
  }, [apartmentIds, orgId]);

  const loadDevices = useCallback(async () => {
    if (!orgId || apartmentIds.length === 0) {
      setDevices([]);
      return;
    }
    setLoadingDevices(true);
    try {
      const [deviceRes, linkRes, cbwRes] = await Promise.all([
        intercomDeviceServiceFind(orgId, undefined, undefined, apartmentIds),
        acsIntegrationClient.listDoorLinks().catch(() => null),
        listCbwDevices(orgId).catch(() => null),
      ]);
      const nextDevices = deviceRes?.data?.intercomDevices || [];
      setDevices(nextDevices);
      const labels: Record<string, string> = {};
      nextDevices.forEach(device => {
        if (device.id && device.integration) {
          labels[device.id] = device.integration;
        }
      });
      (linkRes?.data?.data || []).forEach(link => {
        const provider = String(link.door?.provider || '').toUpperCase();
        if (!link.intercomDeviceId) return;
        if (provider === 'PDK') labels[link.intercomDeviceId] = 'PDK';
        else if (provider === 'BRIVO') labels[link.intercomDeviceId] = 'Brivo';
      });
      (cbwRes?.data?.data?.devices || []).forEach(cbwDevice => {
        (cbwDevice.entrio_cbw_intercom_relay_links || []).forEach(link => {
          const intercomId = String(
            link.entrio_intercom_devices?.id || link.intercom_device_id || ''
          );
          if (intercomId) labels[intercomId] = 'ControlByWeb';
        });
      });
      setIntegrationByDeviceId(labels);
    } catch (error) {
      console.error(error);
      message.error('Failed to load doors.');
      setDevices([]);
    } finally {
      setLoadingDevices(false);
    }
  }, [apartmentIds, orgId]);

  const loadBuildings = useCallback(async () => {
    if (!orgId || apartmentIds.length === 0) {
      setBuildings([]);
      return;
    }
    try {
      const res = await buildingServiceFind(orgId, undefined, apartmentIds);
      setBuildings(res?.data?.buildings || []);
    } catch (error) {
      console.error(error);
      setBuildings([]);
    }
  }, [apartmentIds, orgId]);

  useEffect(() => {
    if (orgId && account.userId) {
      getApartmentList(orgId, account.userId);
    }
  }, [account.userId, orgId]);

  useEffect(() => {
    loadResidents();
    loadDevices();
    loadBuildings();
  }, [loadBuildings, loadDevices, loadResidents]);

  const handlePropertyChange = (nextPropertyId: string) => {
    setPropertyId(nextPropertyId);
    setBuildingId('all');
  };

  const handleFilterReset = () => {
    setPropertyId('all');
    setBuildingId('all');
  };

  const visibleDevices = useMemo(() => {
    return devices.filter(device => {
      const devicePropertyId = device.apartmentId || device.apartment?.id;
      const deviceBuildingId = device.buildingId || device.building?.id;
      if (propertyId !== 'all' && devicePropertyId !== propertyId) return false;
      if (buildingId !== 'all' && deviceBuildingId !== buildingId) return false;
      return true;
    });
  }, [buildingId, devices, propertyId]);

  const handleCall = async (tenant: Tenant) => {
    const roomId = tenant.roomId || tenant.room?.id;
    const userId = tenant.user?.id;
    if (!roomId || !userId) {
      message.error('This resident is missing a unit or user account and cannot be called.');
      return;
    }
    const contextDevice = pickCallContextDevice(devices, tenant);
    if (!contextDevice?.id) {
      message.error('No intercom device is available at this property to place a call.');
      return;
    }

    setCallingTenantId(tenant.id);
    try {
      const res = await callTenantFromFrontDesk({
        deviceId: contextDevice.id,
        roomId,
        user_ids: [userId],
        source: 'FRONT_DESK',
      });
      if (!res.data?.success || !res.data.data?.jwtToken) {
        message.error(res.data?.message || 'Failed to start the call.');
        return;
      }
      setActiveCall({
        tenant,
        callData: res.data.data,
        timeoutSeconds: contextDevice.intercomTimeoutSeconds,
      });
    } catch (error) {
      console.error(error);
      message.error('Failed to start the call.');
    } finally {
      setCallingTenantId(undefined);
    }
  };

  const handleOpenDoor = async (device: IntercomDevice) => {
    if (!device.id) return;
    setOpeningDeviceId(device.id);
    try {
      const res = await openDoorFromFrontDesk({
        deviceId: device.id,
        source: 'FRONT_DESK',
      });
      if (!res.data?.success) {
        message.error(res.data?.message || 'Failed to open the door.');
        return;
      }
      setDoorOpenedName(device.name || 'Door');
    } catch (error) {
      console.error(error);
      message.error('Failed to open the door.');
    } finally {
      setOpeningDeviceId(undefined);
    }
  };

  const handleSetStartPage = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    if (account.userId) dismissFrontDeskStartPageHint(account.userId);
    if (!account.userId || frontDeskIsHome) return;
    setHomePath(account.userId, HOME_PATH_FRONT_DESK);
    setFrontDeskIsHome(true);
    setStartPageToastOpen(true);
  };

  return (
    <Box>
      <PageHeader
        title="Front Desk"
        subtitle="Call residents and control doors from one place."
        titleAdornment={
          <>
            <Tooltip title={frontDeskIsHome ? 'Current start page' : 'Set as start page'}>
              <IconButton
                size="small"
                aria-label={frontDeskIsHome ? 'Current start page' : 'Set as start page'}
                onClick={handleSetStartPage}
                ref={setStartPageHintAnchor}
                sx={{
                  p: 0.5,
                  color: frontDeskIsHome ? 'warning.main' : 'text.disabled',
                  '&:hover': {
                    color: frontDeskIsHome ? 'warning.dark' : 'text.secondary',
                    bgcolor: 'transparent',
                  },
                }}
              >
                {frontDeskIsHome ? (
                  <StarIcon sx={{fontSize: 22}} />
                ) : (
                  <StarBorderIcon sx={{fontSize: 22}} />
                )}
              </IconButton>
            </Tooltip>
            <StartPageHintPopover anchorEl={startPageHintAnchor} />
          </>
        }
      />

      <Box sx={{display: 'flex', gap: 2, alignItems: 'flex-start'}}>
        <Box sx={{flex: 1, minWidth: 0}}>
          <Tabs
            value={tab}
            onChange={(_, value) => setTab(value)}
            sx={{
              mb: 2,
              minHeight: 36,
              '& .MuiTab-root': {minHeight: 36, textTransform: 'none'},
            }}
          >
            <Tab label="Residents" />
            <Tab label="Devices" />
          </Tabs>

          {tab === 0 ? (
            <ResidentsTab
              tenants={tenants}
              apartments={apartments}
              buildings={buildings}
              propertyId={propertyId}
              buildingId={buildingId}
              onPropertyChange={handlePropertyChange}
              onBuildingChange={setBuildingId}
              onReset={handleFilterReset}
              loading={loadingResidents}
              callingTenantId={callingTenantId}
              onCall={handleCall}
            />
          ) : (
            <DevicesTab
              devices={devices}
              apartments={apartments}
              buildings={buildings}
              propertyId={propertyId}
              buildingId={buildingId}
              onPropertyChange={handlePropertyChange}
              onBuildingChange={setBuildingId}
              integrationByDeviceId={integrationByDeviceId}
              loading={loadingDevices}
              openingDeviceId={openingDeviceId}
              onOpenDoor={handleOpenDoor}
            />
          )}
        </Box>

        <Box
          sx={{
            width: {xs: '100%', md: 360},
            flexShrink: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            position: {md: 'sticky'},
            top: {md: 16},
          }}
        >
          <ResidentCallPanel call={activeCall} onEnd={() => setActiveCall(null)} />
          <DoorControlCard
            devices={visibleDevices}
            onViewAll={() => setTab(1)}
            onOpenDoor={handleOpenDoor}
          />
        </Box>
      </Box>

      <Snackbar
        open={startPageToastOpen}
        autoHideDuration={3000}
        onClose={() => setStartPageToastOpen(false)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity="success"
          variant="outlined"
          onClose={() => setStartPageToastOpen(false)}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderColor: 'success.main',
            '& .MuiAlert-icon': {color: 'success.main'},
            '& .MuiAlert-message': {color: 'text.primary'},
            '& .MuiAlert-action': {color: 'text.secondary'},
          }}
        >
          <AlertTitle sx={{color: 'text.primary', fontWeight: 600, mb: 0.25}}>
            Front Desk is now your start page
          </AlertTitle>
          You'll land here after signing in.
        </Alert>
      </Snackbar>

      <Snackbar
        open={Boolean(doorOpenedName)}
        autoHideDuration={4000}
        onClose={() => setDoorOpenedName(null)}
        anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
      >
        <Alert
          severity="success"
          variant="outlined"
          onClose={() => setDoorOpenedName(null)}
          sx={{
            bgcolor: 'background.paper',
            color: 'text.primary',
            borderColor: 'success.main',
            '& .MuiAlert-icon': {color: 'success.main'},
            '& .MuiAlert-message': {color: 'text.primary'},
            '& .MuiAlert-action': {color: 'text.secondary'},
          }}
        >
          <AlertTitle sx={{color: 'text.primary', fontWeight: 600, mb: 0.25}}>
            Door opened
          </AlertTitle>
          {doorOpenedName} is now unlocked.
        </Alert>
      </Snackbar>
    </Box>
  );
}
